// src/resources/bookings/components/BookingActions.tsx
import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Cancel,
  Edit,
  Email,
  Receipt,
  Warning,
  CheckCircle,
} from "@mui/icons-material";
import { useRecordContext, useNotify } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { BOOKING_STATUS_OPTIONS } from "../statuses";

export const BookingActions = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [loading, setLoading] = useState({
    cancel: false,
    status: false,
    voucher: false,
  });

  const handleCancelBooking = async () => {
    if (!record?.id) return;
    setLoading((prev) => ({ ...prev, cancel: true }));

    try {
      const response = await fetch(apiUrl(`/bookings/${record.id}/cancel`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reason: cancelReason || "Cancelled by admin",
        }),
      });

      if (response.ok) {
        notify("Booking cancelled successfully", { type: "success" });
        setCancelDialogOpen(false);
        setCancelReason("");
      } else {
        const error = await response.json();
        notify(error.message || "Failed to cancel booking", { type: "error" });
      }
    } catch {
      notify("Error cancelling booking", { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, cancel: false }));
    }
  };

  const handleUpdateStatus = async () => {
    if (!record?.id) return;
    setLoading((prev) => ({ ...prev, status: true }));

    try {
      const response = await fetch(apiUrl(`/bookings/${record.id}/status`), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: newStatus,
          notes: "Status updated by admin",
        }),
      });

      if (response.ok) {
        notify("Booking status updated successfully", { type: "success" });
        setStatusDialogOpen(false);
        setNewStatus("");
      } else {
        const error = await response.json();
        notify(error.message || "Failed to update status", { type: "error" });
      }
    } catch {
      notify("Error updating status", { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, status: false }));
    }
  };

  const handleResendVoucher = async () => {
    if (!record?.id) return;
    setLoading((prev) => ({ ...prev, voucher: true }));

    try {
      const response = await fetch(
        apiUrl(`/bookings/${record.id}/resend-voucher`),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: record.user?.email || record.user?.username,
          }),
        },
      );

      if (response.ok) {
        notify("Voucher resent successfully", { type: "success" });
        setVoucherDialogOpen(false);
      } else {
        const error = await response.json();
        let errorMessage = "Failed to resend voucher";

        // Handle specific error cases based on API documentation
        if (response.status === 400) {
          errorMessage = "Voucher can only be resent for confirmed bookings";
        } else if (response.status === 403) {
          errorMessage = "Access denied - Admin or SuperAdmin role required";
        } else if (response.status === 404) {
          errorMessage =
            "Booking not found, voucher PDF not found in S3 storage, or customer not found";
        } else if (response.status === 500) {
          errorMessage =
            "Internal server error - Email service failure or S3 storage access error";
        } else if (error.message) {
          errorMessage = error.message;
        }

        notify(errorMessage, { type: "error" });
      }
    } catch {
      notify("Network error - Unable to resend voucher", { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, voucher: false }));
    }
  };

  // Action availability logic. Must match the API's cancellable set
  // (BookingAdminService.cancelBooking): confirmed | started | payment_received | requested_cancellation.
  const canCancel = [
    "confirmed",
    "started",
    "payment_received",
    "requested_cancellation",
  ].includes(record?.status);
  const canResendVoucher = record?.status === "confirmed";
  const canUpdateStatus = true; // Admin can always update status

  // Canonical status list — shared with the API's BOOKING_STATUSES.
  const statusOptions = BOOKING_STATUS_OPTIONS;

  return (
    <Box>
      {/* Action Cards Layout */}
      <Grid container spacing={2}>
        {/* Cancel Booking Card */}
        {canCancel && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                border: "1px solid #ffebee",
                "&:hover": {
                  borderColor: "#f44336",
                  boxShadow: 2,
                },
                cursor: "pointer",
              }}
              onClick={() => setCancelDialogOpen(true)}
            >
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Box sx={{ color: "#f44336", mb: 1 }}>
                  <Cancel sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="h6" color="error" gutterBottom>
                  Cancel Booking
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Cancel this booking and process refund
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Update Status Card */}
        {canUpdateStatus && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                border: "1px solid #e3f2fd",
                "&:hover": {
                  borderColor: "#2196f3",
                  boxShadow: 2,
                },
                cursor: "pointer",
              }}
              onClick={() => setStatusDialogOpen(true)}
            >
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Box sx={{ color: "#2196f3", mb: 1 }}>
                  <Edit sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  Update Status
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Change booking status
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Resend Voucher Card */}
        {canResendVoucher && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                border: "1px solid #e8f5e8",
                "&:hover": {
                  borderColor: "#4caf50",
                  boxShadow: 2,
                },
                cursor: "pointer",
              }}
              onClick={() => setVoucherDialogOpen(true)}
            >
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Box sx={{ color: "#4caf50", mb: 1 }}>
                  <Receipt sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="h6" color="success.main" gutterBottom>
                  Resend Voucher
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Send booking voucher to customer
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Cancel Booking Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning color="error" />
          Cancel Booking
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will cancel the booking and may trigger a refund. Please
            ensure this is the correct action.
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Booking Details:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Booking ID: {record?.id}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Customer: {record?.user?.name || record?.user?.username}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Amount: ${record?.amountB2B2C || 0}
            </Typography>
          </Box>

          <MuiTextField
            autoFocus
            margin="dense"
            label="Cancellation Reason"
            fullWidth
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={3}
            placeholder="Please provide a reason for cancellation..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={loading.cancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCancelBooking}
            color="error"
            disabled={loading.cancel || !cancelReason.trim()}
            startIcon={
              loading.cancel ? <CircularProgress size={16} /> : <Cancel />
            }
          >
            {loading.cancel ? "Cancelling..." : "Confirm Cancellation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Edit color="primary" />
          Update Booking Status
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Status:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {record?.status
                ?.replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </Typography>
          </Box>

          <FormControl fullWidth margin="dense">
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="New Status"
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body1">{option.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStatusDialogOpen(false)}
            disabled={loading.status}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            color="primary"
            disabled={loading.status || !newStatus}
            startIcon={
              loading.status ? <CircularProgress size={16} /> : <CheckCircle />
            }
          >
            {loading.status ? "Updating..." : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Voucher Dialog */}
      <Dialog
        open={voucherDialogOpen}
        onClose={() => setVoucherDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Receipt color="success" />
          Resend Booking Voucher
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will send the booking voucher to the customer&apos;s email
            address. The voucher must exist in S3 storage.
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Booking Details:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Booking ID: {record?.id}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Customer: {record?.user?.name || record?.user?.username}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Email: {record?.user?.email || record?.user?.username}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Property: {record?.property?.name}
            </Typography>
          </Box>

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Requirements:</strong>
              <br />• Booking status must be &quot;confirmed&quot;
              <br />• Voucher PDF must exist in S3 storage
              <br />• Customer email must be valid
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setVoucherDialogOpen(false)}
            disabled={loading.voucher}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResendVoucher}
            color="success"
            disabled={loading.voucher}
            startIcon={
              loading.voucher ? <CircularProgress size={16} /> : <Email />
            }
          >
            {loading.voucher ? "Sending..." : "Resend Voucher"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
